// packages/web/src/process/ProcessMode.tsx
import type { Process, ArchitectureModel, StableId } from "@archlang/scene/arch-model";
import { useNodeColoring } from "../board/coloring/useNodeColoring.js";
import { ownerColorForModel } from "./ownerColorFor.js";
import type {
  WalkResult,
  WalkStep,
  WalkerState,
  SwimlaneContext,
  NodeId,
  StepLatency,
  ProcessEnd,
} from "@archlang/engine";
import { Button } from "@archlang/ui";
import { EndPicker } from "./EndPicker.js";
import { useProcessLayouts } from "./useProcessLayouts.js";
import { useLayoutParam, type ProcessLayout } from "./useLayoutParam.js";
import { useCallback, useEffect, useMemo, type ReactNode } from "react";
import { SwimlaneView } from "./SwimlaneView.js";
import { BpmnView } from "./BpmnView.js";
import { ReorderBadge } from "./ReorderBadge.js";
import type { BpmnLaneEditApi } from "./bpmnLaneEdit.js";
import type { ProcessStepEditApi } from "./processStepEdit.js";
import { applyDraftLanes } from "./processLaneDraft.js";
import type { EvalContext, FlowBodyClause } from "@archlang/engine";
import { ProcessTree } from "./ProcessTree.js";
import { StepScrubber } from "./StepScrubber.js";
import { useStepEdgeStyleResolver } from "./useStepEdgeStyleResolver.js";
import type { PathGate } from "./pathGates.js";
import {
  useProcessSelection,
  orderedStepIds,
  type ProcessSelection,
} from "./processSelection.js";
import { ProcessSelectionToolbar } from "./ProcessSelectionToolbar.js";
import { selectedStepsText } from "./stepLineText.js";
import {
  copyText,
  readText,
  clipboardStepLines,
  type ClipboardLike,
} from "./processClipboard.js";

export interface ProcessModeProps {
  readonly model: ArchitectureModel;
  readonly activeProcessId: StableId | null;
  readonly activeProcess: Process | null;
  readonly walkResult: WalkResult | null;
  readonly walkResultAllBranches: WalkResult | null;
  readonly walkerState: WalkerState;
  readonly activeStepIndex: number | null;
  readonly autoPlay: boolean;
  readonly setAutoPlay: (b: boolean) => void;
  /** Advance to the next step — the view calls this to drive auto-play cadence
   *  (after the travel droplet reaches the node + a dwell). */
  readonly onAutoAdvance: () => void;
  readonly onSelectProcess: (id: StableId | null) => void;
  readonly onSelectStepIndex: (i: number | null) => void;
  readonly onBranchSelect: (nodeId: NodeId, label: string) => void;
  readonly onSubprocessToggle: (nodeId: NodeId, expanded: boolean) => void;
  readonly onSelectModule: (qualified: string) => void;
  readonly swimlaneContext: SwimlaneContext;
  /** Path gating (see useProcessPlayback) — the scrubber asks at unresolved
   *  exclusive frames instead of silently walking the default arm. */
  readonly onGateAdvance?: (toIndex: number) => boolean;
  readonly pendingChoice?: PathGate | null;
  readonly onChoosePath?: (label: string) => void;
  readonly onDismissChoice?: () => void;
  /** "Colour by" picker slot — rendered in the header next to the layout
   *  toggle (the host wires it to the shared coloring store). */
  readonly colorControls?: ReactNode;
  /** v0.10.2 latency estimate for the active process (`120ms – 2s · …`),
   *  rendered as a header badge; null hides it (PROCESS.md §13). */
  readonly latencyBadge?: string | null;
  /** v0.10.2 per-step latencies keyed by walk stepIndex — the sequence-view
   *  ruler (cumulative) + per-hop arrow labels. */
  readonly stepLatencies?: ReadonlyMap<number, StepLatency>;
  /** Live process-conformance: walk stepIndexes observed to run OUT OF ORDER
   *  vs the model (the "reordered" drift). The swimlane marks these steps. */
  readonly reorderedSteps?: ReadonlySet<number>;
  /** Live process-conformance counts driving the header summary badge:
   *  sampled traces that ran steps out of the modeled order, vs. those that
   *  ran the modeled order exactly. Badge shows only when reorderedTraces > 0. */
  readonly reorderedTraces?: number;
  readonly conformingTraces?: number;
  /** v0.10.2 process ends (≥2) — the end picker; empty hides it. */
  readonly ends?: readonly ProcessEnd[];
  /** Colour legend slot — floats bottom-left over the diagram (BPMN layout;
   *  the same ColorLegend widget the board mounts). */
  readonly legend?: ReactNode;
  /** BPMN lane editing on the process viewer. The process diagram is a
   *  synthesized, editable view: this edits its EPHEMERAL lane draft in-browser
   *  (add/rename/merge/pool) + step→lane reassignment (a real owner edit). Null
   *  keeps the bpmn view read-only. */
  readonly bpmnEdit?: BpmnLaneEditApi;
  /** WYSIWYG step editing — shared across all four process surfaces (tree
   *  spine, flow/sequence, BPMN). Read-only when absent / `canEdit` false. The
   *  affordances only surface when `editMode` is on (the per-view stance). */
  readonly stepEdit?: ProcessStepEditApi;
  /** The per-view edit stance. When false (viewing), the WYSIWYG step
   *  affordances stay hidden even if `stepEdit` is wired — click is navigate.
   *  Toggled by {@link editToggle}. */
  readonly editMode?: boolean;
  /** The view↔edit toggle button, rendered in the header. Host wires it to the
   *  per-view EditMode context (absent → no toggle, e.g. non-editable hosts). */
  readonly editToggle?: ReactNode;
  /** The parsed ephemeral lane-draft body — applied as a pure post-pass over the
   *  live bpmn layout (playback preserved). Empty/undefined = the plain default. */
  readonly laneDraftBody?: readonly FlowBodyClause[];
  /** Lane-selector node universe (`buildNodeUniverse(model)`), memoized by the
   *  host — the post-pass needs it to resolve `lane by @@key` / `lane <ref>`. */
  readonly laneUniverse?: EvalContext["universe"] | null;
  /** "Save as view" — promotes the current ephemeral lane draft to a durable
   *  `view <P>Bpmn { flow <P> bpmn { … } }` and opens it. Shown on bpmn layout
   *  only when the host can write the model. */
  readonly onCreateBpmnView?: () => void;
  /** SHARED step selection (P5 #34), owned by the host (ProcessPane) so the
   *  tree, the flow/sequence board, AND the #26 keyboard drive ONE selection.
   *  ProcessMode threads it into both surfaces and renders the ONE floating
   *  toolbar over them (in edit mode). Absent ⇒ each surface owns a local one. */
  readonly selection?: ProcessSelection;
  /** Test/embedder clipboard override for the toolbar's Copy/Paste. */
  readonly clipboard?: ClipboardLike;
}

/**
 * Owner-path → colour, bridging the unified node-coloring layer to the BPMN
 * view's owner paths. The pure mapping lives in ownerColorFor.ts (shared with
 * the process image export); this hook just binds it to the coloring context.
 */
function useOwnerColor(
  model: ArchitectureModel,
): ((path: readonly string[]) => string | undefined) | undefined {
  const coloring = useNodeColoring();
  return useMemo(
    () => (coloring ? ownerColorForModel(model, coloring) : undefined),
    [coloring, model],
  );
}

export function ProcessMode(props: ProcessModeProps) {
  const { layout, setLayout } = useLayoutParam();
  const ownerColor = useOwnerColor(props.model);
  const { flow, sequence, bpmn: bpmnBase } = useProcessLayouts({
    walkResult: props.walkResult,
    walkResultAllBranches: props.walkResultAllBranches,
    swimlaneContext: props.swimlaneContext,
  });
  // The process bpmn diagram is a synthesized, editable view: its ephemeral lane
  // draft is applied as a PURE post-pass over the live-walk layout (assignBpmnLanes
  // only remaps y, so active-step / branch-dim playback is untouched).
  const { laneDraftBody, laneUniverse } = props;
  const bpmn = useMemo(
    () =>
      bpmnBase && laneDraftBody && laneDraftBody.length > 0 && laneUniverse
        ? applyDraftLanes(bpmnBase, laneDraftBody, laneUniverse)
        : bpmnBase,
    [bpmnBase, laneDraftBody, laneUniverse],
  );
  // The resolver must cover the walk the views RENDER — the all-branches one.
  // Built from the selected walk, steps living only in unselected bands miss
  // byIndex and lose all edge styling (comp orange, dist, interface colors).
  const resolveStepEdgeStyle = useStepEdgeStyleResolver(
    props.model,
    props.walkResultAllBranches ?? props.walkResult,
  );
  // Tree/BPMN views have no travel droplet to drive the cadence — fall back
  // to a steady auto-play tick. Flow/sequence drive their own cadence (after
  // the droplet reaches each node), via SwimlaneView's onAutoAdvance.
  const { autoPlay, onAutoAdvance } = props;
  useEffect(() => {
    if ((layout !== "tree" && layout !== "bpmn") || !autoPlay) return;
    const t = window.setInterval(() => onAutoAdvance(), 900);
    return () => window.clearInterval(t);
  }, [layout, autoPlay, onAutoAdvance]);

  // The WYSIWYG affordances only surface in edit mode (the per-view stance);
  // in view mode clicks stay navigate/select even when `stepEdit` is wired.
  const stepEdit = props.editMode ? props.stepEdit : undefined;

  // SHARED step selection (P5 #34) — the tree and the flow/sequence board drive
  // ONE selection; the ONE floating toolbar (rendered here) + the #26 keyboard
  // (in ProcessPane) act on it. The host may inject it (so the keyboard reads the
  // same object); otherwise ProcessMode owns a local one. Selectable rows range
  // over the ALL-BRANCHES walk so a swimlane Shift-range covers every shown step;
  // the tree (present path) ranges over the subset it renders (indexOf no-op for
  // a hidden nodeId).
  const localSelection = useProcessSelection();
  const selection = props.selection ?? localSelection;
  const selectionWalk = props.walkResultAllBranches ?? props.walkResult;
  const stepOrder = useMemo(() => orderedStepIds(selectionWalk), [selectionWalk]);
  const { selected, count, clear } = selection;
  const clipboard = props.clipboard;

  // nodeId → WalkStep, for the Copy text (built once per walk).
  const stepByNodeIdMap = useMemo(() => {
    const m = new Map<NodeId, WalkStep>();
    for (const s of selectionWalk?.steps ?? []) m.set(s.nodeId, s);
    return m;
  }, [selectionWalk]);
  const stepByNodeId = useCallback((id: NodeId) => stepByNodeIdMap.get(id), [stepByNodeIdMap]);

  const onSelCopy = useCallback(() => {
    void copyText(selectedStepsText(stepOrder, selected, stepByNodeId), clipboard);
  }, [stepOrder, selected, stepByNodeId, clipboard]);

  const onSelDelete = useCallback(() => {
    if (!stepEdit) return;
    // Document order → deterministic batch (one journaled mutation, one undo).
    // Clear only when the batch actually applied (a refused delete keeps the
    // rows selected so the failure isn't swallowed) — mirrors the tree exactly.
    const ids = stepOrder.filter((id) => selected.has(id));
    if (ids.length === 0) return;
    if (stepEdit.deleteSteps(ids)) clear();
  }, [stepEdit, stepOrder, selected, clear]);

  const selPasteAnchor = useMemo<NodeId | null>(() => {
    let last: NodeId | null = null;
    for (const id of stepOrder) if (selected.has(id)) last = id;
    return last;
  }, [stepOrder, selected]);

  const onSelPaste = useCallback(() => {
    if (!stepEdit || !selPasteAnchor) return;
    void (async () => {
      const text = await readText(clipboard);
      if (!text) return;
      const lines = clipboardStepLines(text);
      if (lines.length > 0) stepEdit.addStepAfter(selPasteAnchor, lines.join("\n"));
    })();
  }, [stepEdit, selPasteAnchor, clipboard]);

  if (props.model.processes.length === 0) {
    return <Empty message="No processes declared in this workspace." />;
  }
  if (!props.activeProcess || !props.walkResult) {
    return (
      <div className="p-6">
        <p className="text-slate-400">Pick a process from the workspace panel.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full flex-1 min-w-0">
      <div className="flex items-center gap-3 px-4 py-2 border-b border-slate-800 bg-slate-900/(--tint-dim)">
        {props.editToggle}
        <LayoutToggle layout={layout} setLayout={setLayout} />
        {layout === "bpmn" && props.onCreateBpmnView && (
          <Button
            variant="secondary"
            size="sm"
            onClick={props.onCreateBpmnView}
            title="Save the current (ephemeral) lanes as a durable bpmn view and open it"
          >
            Save as view
          </Button>
        )}
        {props.colorControls}
        {props.latencyBadge && (
          <span
            className="rounded border border-slate-700 bg-slate-800/(--tint-strong) px-2 py-0.5 text-xs font-medium text-slate-300 tabular-nums"
            title="Estimated process time (PROCESS.md §13 — a static requirement-based estimate, not a measurement)"
          >
            {props.latencyBadge}
          </span>
        )}
        <ReorderBadge reorderedTraces={props.reorderedTraces} conformingTraces={props.conformingTraces} />
        {props.ends && props.ends.length > 0 && (
          // Remount per process so the picker's local selection resets to the
          // longest end when you switch processes (no stale index).
          <EndPicker key={props.activeProcessId ?? ""} ends={props.ends} />
        )}
        {/* Playback scrubber is a VIEW-mode HUD. In edit mode there is no
            scrubber — a click selects the step to edit (highlight), not a
            playback position (P5 #34). */}
        {!props.editMode && (
          <div className="ml-auto">
            <StepScrubber
              walkResult={props.walkResult}
              stepIndex={props.activeStepIndex}
              setStepIndex={props.onSelectStepIndex}
              autoPlay={props.autoPlay}
              setAutoPlay={props.setAutoPlay}
              onGateAdvance={props.onGateAdvance}
              pendingChoice={props.pendingChoice}
              onChoosePath={props.onChoosePath}
              onDismissChoice={props.onDismissChoice}
            />
          </div>
        )}
      </div>
      <div className="flex-1 min-h-0 relative">
        {props.legend}
        {layout === "flow" && flow && (
          <SwimlaneView
            layout={flow}
            walkResult={props.walkResultAllBranches ?? props.walkResult}
            walkerState={props.walkerState}
            activeStepIndex={props.activeStepIndex}
            onSelectStepIndex={(i) => props.onSelectStepIndex(i)}
            onBranchSelect={props.onBranchSelect}
            onSubprocessToggle={props.onSubprocessToggle}
            onSelectModule={props.onSelectModule}
            resolveStepEdgeStyle={resolveStepEdgeStyle}
            autoPlay={props.autoPlay}
            onAutoAdvance={props.onAutoAdvance}
            ownerColor={ownerColor}
            stepLatencies={props.stepLatencies}
            reorderedSteps={props.reorderedSteps}
            stepEdit={stepEdit}
            selection={selection}
            stepOrder={stepOrder}
          />
        )}
        {layout === "sequence" && sequence && (
          <SwimlaneView
            layout={sequence}
            walkResult={props.walkResultAllBranches ?? props.walkResult}
            walkerState={props.walkerState}
            activeStepIndex={props.activeStepIndex}
            onSelectStepIndex={(i) => props.onSelectStepIndex(i)}
            onBranchSelect={props.onBranchSelect}
            onSubprocessToggle={props.onSubprocessToggle}
            onSelectModule={props.onSelectModule}
            resolveStepEdgeStyle={resolveStepEdgeStyle}
            autoPlay={props.autoPlay}
            onAutoAdvance={props.onAutoAdvance}
            ownerColor={ownerColor}
            stepLatencies={props.stepLatencies}
            reorderedSteps={props.reorderedSteps}
            stepEdit={stepEdit}
            selection={selection}
            stepOrder={stepOrder}
          />
        )}
        {layout === "bpmn" && bpmn && (
          <BpmnView
            layout={bpmn}
            activeStepIndex={props.activeStepIndex}
            onSelectStepIndex={(i) => props.onSelectStepIndex(i)}
            onSubprocessToggle={props.onSubprocessToggle}
            onSelectModule={props.onSelectModule}
            onBranchSelect={props.onBranchSelect}
            ownerColor={ownerColor}
            edit={props.bpmnEdit}
            resolveStepEdgeStyle={resolveStepEdgeStyle}
            stepLatencies={props.stepLatencies}
            reorderedSteps={props.reorderedSteps}
            stepEdit={stepEdit}
          />
        )}
        {layout === "tree" && (
          <div className="h-full overflow-y-auto">
            <ProcessTree
              // Process (scene model) ⊇ ProcessDecl shape the tree reads.
              process={props.activeProcess as never}
              walkResult={props.walkResult}
              activeStepIndex={props.activeStepIndex}
              expandedSubprocesses={props.walkerState.expandedSubprocesses}
              branchSelection={props.walkerState.branchSelection}
              onSelectStepIndex={(i) => props.onSelectStepIndex(i)}
              onSelectModule={props.onSelectModule}
              onBranchSelect={props.onBranchSelect}
              onSubprocessToggle={props.onSubprocessToggle}
              ownerColor={ownerColor}
              stepEdit={stepEdit}
              selection={selection}
              clipboard={clipboard}
            />
          </div>
        )}
      </div>
      {/* The ONE floating selection toolbar, shared across the tree and the
          flow/sequence board (P5 #34). Rendered in edit mode when ≥1 step is
          selected; wired to the SAME deleteSteps + clipboard the tree used. */}
      {stepEdit?.canEdit && (
        <ProcessSelectionToolbar
          count={count}
          onDelete={onSelDelete}
          onCopy={onSelCopy}
          onPaste={selPasteAnchor ? onSelPaste : null}
          onClear={clear}
        />
      )}
    </div>
  );
}

function Empty({ message }: { message: string }) {
  return <div className="flex items-center justify-center h-full text-slate-500">{message}</div>;
}

function LayoutToggle({
  layout,
  setLayout,
}: {
  layout: ProcessLayout;
  setLayout: (l: ProcessLayout) => void;
}) {
  return (
    <div className="inline-flex rounded-sm border border-slate-700 overflow-hidden">
      <ToggleButton active={layout === "flow"} onClick={() => setLayout("flow")} label="Flow" />
      <ToggleButton active={layout === "sequence"} onClick={() => setLayout("sequence")} label="Sequence" />
      <ToggleButton active={layout === "bpmn"} onClick={() => setLayout("bpmn")} label="BPMN" />
      <ToggleButton active={layout === "tree"} onClick={() => setLayout("tree")} label="Tree" />
    </div>
  );
}

function ToggleButton({
  active,
  onClick,
  label,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
}) {
  return (
    // A joined segment of the layout strip: the kit's `rounded-md` + variant
    // background would break the seamless strip, so the segment keeps its own
    // active/idle paint and only borrows the kit's sizing + focus ring.
    <button
      onClick={onClick}
      className={
        "inline-flex items-center justify-center h-7 px-3 text-xs font-medium transition-colors " +
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 " +
        (active ? "bg-sky-500/25 text-sky-100" : "bg-slate-900 text-slate-300 hover:bg-slate-800")
      }
    >
      {label}
    </button>
  );
}
